/**
 * TrailingStopLossDialog
 *
 * Guided first-time entry for a leg's Trailing Stop Loss — two values
 * ("if instrument moves X" / "then move stop loss by Y") + a unit, with a
 * live plain-English summary. Opened from the matrix's "+ Trail" affordance;
 * once saved, the matrix cell shows the compact inline editor.
 *
 * Mirrors the shared `Modal` look (rounded-lg / shadow-xl / dark:bg-gray-800,
 * fade-zoom in) and uses the shared white-label-aware `Button`, but renders a
 * self-contained `z-[60]` overlay so it stacks above the editor modal without
 * the shared Modal's document-level Escape handler fighting the editor's.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { isWhiteLabel } from '../../../config/tenantConfig';
import Button from '../../ui/Button';
import Dropdown from '../../ui/Dropdown';
import FocusedDialog from '../../ui/FocusedDialog';
import { TARGET_PROFIT_TYPE_OPTIONS } from '../../../utils/strategy/strategyConstants';
import { TSL_TYPE } from '../../../constants/tradingConstants';
import { TslType } from '../../../types/strategy';

// A trailing SL moves the leg's own stop, so its unit family follows the SL:
// a premium SL trails in premium points / %, a UL (index-spot) SL trails in
// index points / % of spot. A premium trail on a UL SL would never move.
type DialogTslType = TslType;
const UL_TSL_OPTIONS = [
  { value: TSL_TYPE.UL_POINTS, label: 'Index pts' },
  { value: TSL_TYPE.UL_PERCENTAGE, label: 'Index %' },
];

interface TslValue {
  type: DialogTslType;
  instrumentMoveValue: number;
  stopLossMoveValue: number;
}

interface TrailingStopLossDialogProps {
  initial: TslValue;
  /** The leg's SL is UL (index-spot) — offer UL trail units only. */
  ulStopLoss?: boolean;
  onSave: (v: TslValue) => void;
  onCancel: () => void;
}

const wl = isWhiteLabel();
const focusRing = wl ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
const unitLabel = (t: DialogTslType) => {
  if (t === TSL_TYPE.PERCENTAGE || t === TSL_TYPE.UL_PERCENTAGE) return '%';
  return t === TSL_TYPE.UL_POINTS ? 'index pts' : 'pts';
};

const TrailingStopLossDialog: React.FC<TrailingStopLossDialogProps> = ({ initial, ulStopLoss = false, onSave, onCancel }) => {
  const options = ulStopLoss ? UL_TSL_OPTIONS : TARGET_PROFIT_TYPE_OPTIONS;
  const allowed = options.map((o) => o.value as DialogTslType);
  // Coerce a type from the other family to this family's first unit.
  const [type, setType] = useState<DialogTslType>(
    allowed.includes(initial.type) ? initial.type : allowed[0],
  );
  const [instr, setInstr] = useState<number>(initial.instrumentMoveValue || 0);
  const [sl, setSl] = useState<number>(initial.stopLossMoveValue || 0);

  const numInput = (v: number, set: (n: number) => void) => (
    <input
      type="number"
      min={0}
      step={0.1}
      value={Number.isFinite(v) ? v : 0}
      onChange={(e) => {
        const p = parseFloat(e.target.value);
        set(Number.isNaN(p) ? 0 : Math.max(0, p));
      }}
      className={`w-full text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 ${focusRing}`}
    />
  );

  const unitSel = () => (
    <Dropdown portal value={type} options={options} onChange={(v) => setType(v as DialogTslType)} className="w-32" />
  );

  const valid = instr > 0 && sl > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} aria-hidden />
      <FocusedDialog
        ariaLabel="Trailing Stop Loss"
        className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col animate-in fade-in zoom-in-95 duration-150"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Trailing Stop Loss</h2>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-3">
          <div className="flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 px-4 py-3">
            <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-200">{ulStopLoss ? 'If index moves' : 'If instrument moves'}</span>
            <div className="w-24">{numInput(instr, setInstr)}</div>
            {unitSel()}
          </div>
          <div className="flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 px-4 py-3">
            <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-200">Then move stop loss by</span>
            <div className="w-24">{numInput(sl, setSl)}</div>
            {unitSel()}
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-1">
            Every time the {ulStopLoss ? 'index' : 'instrument'} moves{' '}
            <span className="font-semibold text-gray-700 dark:text-gray-200">{instr || 0} {unitLabel(type)}</span>{' '}
            in the profit direction, move the stop loss{' '}
            <span className="font-semibold text-gray-700 dark:text-gray-200">{sl || 0} {unitLabel(type)}</span>{' '}
            in the same direction.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => onSave({ type, instrumentMoveValue: instr, stopLossMoveValue: sl })}
            disabled={!valid}
          >
            Save
          </Button>
        </div>
      </FocusedDialog>
    </div>
  );
};

export default TrailingStopLossDialog;
